import { Component, OnDestroy, OnInit } from '@angular/core';
import { ICard, IContract } from '@app/shared/models/contract.model';
import { CARD_COMPONENT } from '@app/shared/modules/buttons-actions/components/card-actions-buttons/card-modal.enum';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { NotificationService } from '@app/shared/services/notification.service';
import { OrderService } from '@app/shared/services/onboarding/order.service';
import { CONTRACT_TYPE, CARD_TYPE } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { AuthService, User } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, Subject, switchMap, take, takeUntil, tap, throwError } from 'rxjs';

@Component({
  selector: 'app-card-restart-resend',
  templateUrl: './card-restart-resend.component.html',
  styleUrls: ['./card-restart-resend.component.sass'],
})
export class CardRestartResendComponent extends SubscriberComponent implements OnInit, OnDestroy {
  contract!: IContract | null;
  contractType!: CONTRACT_TYPE;
  cardType!: CARD_TYPE;
  initialState!: IDynamicDialogConfig;
  currentCard!: ICard | undefined;
  private closeModal$ = new Subject<void>();

  constructor(
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _modalSvc: ModalService,
    public readonly _contractContextService: ContractContext,
    private readonly _authSvc: AuthService,
    private readonly _notificationSvc: NotificationService,
    private readonly _translateSvc: TranslateService,
    private readonly _orderdSvc: OrderService
  ) {
    super();
  }

  ngOnInit() {
    this.logInitEvent();
    this.getModalData();
    this.confirmActionSubscription();
    this.setupModalCloseSubscription();
  }

  private setupModalCloseSubscription(): void {
    this._subscriptions.sink = this._modalSvc
      .getModalData()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data),
        switchMap(() => this._modalSvc['_dialogRef'].afterClosed())
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.RESTART_RESEND_CARD,
    });
  }

  getModalData() {
    this._subscriptions.sink = this._modalSvc
      .getModalData()
      .pipe(tap(data => (this.initialState = data)))
      .subscribe(() => this.getContractAndCard());
  }

  private confirmActionSubscription() {
    this._modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.closeModal$),
        filter(value => value),
        switchMap(() => this._authSvc.user$),
        switchMap((agentData: User | null | undefined) =>
          this._orderdSvc.updateOrderStatus(this.currentCard?.code || '', agentData?.email || '')
        ),
        tap(() => this.handleRestartOrderSuccess()),
        catchError(() => this.handleReplaceCardError())
      )
      .subscribe();
  }

  getContractAndCard() {
    this.contract = this._contractContextService.getCurrentContract();
    this.contractType = this.contract?.type === CONTRACT_TYPE.CREDIT ? CONTRACT_TYPE.CREDIT : CONTRACT_TYPE.DEBIT;

    let cardCode = this.initialState?.modalComponentData?.cardCode;

    this.currentCard = this.contract?.cards.find(card => card.code === cardCode);
    this.cardType = this.currentCard?.type === CARD_TYPE.VIRTUAL ? CARD_TYPE.VIRTUAL : CARD_TYPE.PHYSICAL;
  }

  handleReplaceCardError() {
    return throwError(() => {
      this._modalSvc.closeModal();
      this.notification(this._translateSvc.instant('MODAL_CARDS_RESTART_RESEND.SNACK_BAR.ERROR'), 3000, 'error', ['snackbar-failure']);
    });
  }

  handleRestartOrderSuccess() {
    this._modalSvc.closeModal();
    this.notification(
      this._translateSvc.instant('MODAL_CARDS_RESTART_RESEND.SNACK_BAR.SUCCESS'),
      3000,
      'green_check',
      ['snackbar-success'],
      true
    );
  }

  notification(message: string, time: number, icon: string, theme: string[], isSVG = false) {
    this._notificationSvc.openSnackBar(message, time, undefined, undefined, icon, theme, isSVG);
  }

  closeModal() {
    this.closeModal$.next();
    this.closeModal$.complete();
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.unsubscribe();
  }
}
